<template>
    <div class="gift-main-container">
        <div class="inputs">
            <div class="start-input">
                <div class="name-div">
                    <label>Recipient Name</label>
                    <prime-input-text v-model="name" class="name-input" placeholder="John Doe" @keydown.enter="onEnter" @keydown="letterOnlyInputHandler" />
                </div>
                <div class="amount-div">
                    <label>Gift Certificate Amount</label>
                    <div class="amount-container" :class="[showAmountError ? 'error' : '', 'border-zinc-300']">
                        <div class="amount-error" :class="showAmountError ? 'border-r-red-300' : 'border-r-zinc-300'">
                            <p>$</p>
                        </div>
                        <prime-input-text
                            v-model="amountInput"
                            class="amount-input"
                            placeholder="50.00"
                            @input="validateNumericInput"
                            @keydown="preventNonNumericKeys"
                            @keydown.enter="onEnter"
                        />
                    </div>
                    <p v-if="showAmountError" class="amount-warning">Amount must be at least $20.00</p>
                    <p v-else-if="showAmountTooHighError" class="amount-warning">Amount cannot exceed $500.00</p>
                    <p v-else class="amount-info">Minimum amount: $20.00</p>
                </div>
            </div>
            <div class="email-div">
                <div class="email-sub-div">
                    <label>Recipient Email</label>
                    <div class="w-full">
                        <prime-input-text
                            v-model="email"
                            class="email-input"
                            placeholder="johndoe@email.com"
                            @keydown.enter="onEnter"
                            :invalid="email.trim() !== '' && !isValidEmail(email.trim())"
                        />
                        <div v-if="email.trim() !== '' && !isValidEmail(email.trim())" class="validation-message">
                            <span>Please enter a valid email address.</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="summary-section">
            <div class="summary-header">
                <p>Summary</p>
            </div>
        </div>
        <div class="summary-footer">
            <span>Due Today</span>
            <p>{{ formattedAmount }}</p>
        </div>
        <footer-gift-cert
            :isFormValid="isFormValid"
            :isLoading="isLoading"
            @purchase="handlePurchase"
            @update:isChecked="isChecked = $event"
        />
    </div>
</template>
<script setup lang="ts">
import { useSubscribeStore } from '@/stores/components/modals/subscription-modal';
import PrimeInputText from 'primevue/inputtext';
import { ref } from 'vue';
import { getDialogRef, useDynamicDialog } from '~/composables/dynamic-dialog';
import { useAuthStore } from '~/stores/auth';
import { useBillingStore } from '~/stores/person/billing';
import { ModalNames } from '~/types/modals';
import FooterGiftCert from './FooterGiftCert.vue';

const { formatCurrency, letterOnlyInputHandler, isValidEmail } = useUtils();
const store = useSubscribeStore();
const billingStore = useBillingStore();
const authStore = useAuthStore();
const dialogRef = getDialogRef(ModalNames.GIFTCERT);

const name = ref('');
const email = ref('');
const amountInput = ref('');
const isChecked = ref(false);
const isLoading = ref(false);

const amount = computed(() => {
    const parsed = parseFloat(amountInput.value);
    return isNaN(parsed) ? 0 : parsed;
});

const formattedAmount = computed(() => {
    return formatCurrency(amount.value * 100);
});

const isFormValid = computed(() => {
    const valid =
        name.value.trim() !== '' && email.value.trim() !== '' && amount.value >= 20 && amount.value <= 500 && isChecked.value && isValidEmail(email.value.trim());

    return valid;
});

const showAmountError = computed(() => {
    return amount.value > 0 && amount.value < 20;
});

const showAmountTooHighError = computed(() => {
    return amount.value > 500;
});

const validateNumericInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    // Remove any non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');

    // Ensure only one decimal point
    const parts = numericValue.split('.');
    if (parts.length > 2) {
        const filteredValue = parts[0] + '.' + parts.slice(1).join('');
        target.value = filteredValue;
        amountInput.value = filteredValue;
    } else {
        // Limit decimal places to maximum of 2
        if (parts.length === 2 && parts[1].length > 2) {
            const filteredValue = parts[0] + '.' + parts[1].substring(0, 2);
            target.value = filteredValue;
            amountInput.value = filteredValue;
        } else {
            target.value = numericValue;
            amountInput.value = numericValue;
        }
    }
};

const preventNonNumericKeys = (event: KeyboardEvent) => {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    const cursorPosition = target.selectionStart || 0;

    // Allow: backspace, delete, tab, escape, enter, arrow keys
    if ([8, 9, 27, 13, 37, 38, 39, 40, 46].includes(event.keyCode)) {
        return;
    }

    // Allow: numbers 0-9
    if ((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105)) {
        // Check if adding this number would exceed 2 decimal places
        if (value.includes('.')) {
            const decimalIndex = value.indexOf('.');
            const decimalPlaces = cursorPosition - decimalIndex - 1;
            if (decimalPlaces >= 2) {
                event.preventDefault();
                return;
            }
        }
        return;
    }

    // Allow: decimal point (but only one)
    if (event.keyCode === 190 || event.keyCode === 110) {
        if (value.includes('.')) {
            event.preventDefault();
            return;
        }
        return;
    }

    // Prevent all other keys
    event.preventDefault();
};

const handlePurchase = async () => {
    isLoading.value = true;
    try {
        // Check if user has a payment method
        if (!authStore.user?.identity?.payment_method?.type) {
            // No payment method - show payment modal
            handleShowPaymentModal();
            return;
        }

        // Has payment method - proceed with purchase
        const result = await billingStore.purchaseGiftCertificate(name.value, email.value, amount.value);
        store.setGiftCertificateDetails(result.data.code, email.value, name.value, result.data.amount);
        await billingStore.fetchInvoices();

        dialogRef?.value?.close();
        useDynamicDialog(ModalNames.GIFTCERT_PURCHASED, undefined, { dismissableMask: false });
    } catch (error: any) {
        console.error('Failed to purchase gift certificate:', error);

        // Check if the error is about no payment method available
        if (error.message?.includes('No payment method available') || error.message?.includes('No payment method')) {
            // Show payment modal for this error
            handleShowPaymentModal();
            return;
        }

        // Don't proceed to the purchased modal if there's an error
        return;
    } finally {
        isLoading.value = false;
    }
};

const handleShowPaymentModal = () => {
    // Reset loading state since we're showing the payment modal
    isLoading.value = false;

    // Listen for payment success using Nuxt hook system
    const nuxtApp = useNuxtApp();
    const hook = nuxtApp.hook('payment_method:success', async (payload: { paymentMethodId: string }) => {
        try {
            // Proceed with gift certificate purchase using the new payment method
            const result = await billingStore.purchaseGiftCertificate(
                name.value,
                email.value,
                amount.value,
                payload.paymentMethodId
            );
            store.setGiftCertificateDetails(result.data.code, email.value, name.value, result.data.amount);
            dialogRef?.value?.close();
            useDynamicDialog(ModalNames.GIFTCERT_PURCHASED);
        } catch (error) {
            console.error('Failed to purchase gift certificate:', error);
        }
        // Remove the hook after it's used
        hook();
    });

    useDynamicDialog(ModalNames.STRIPE_PAYMENT);
};

const onEnter = (e: KeyboardEvent) => {
    if (isFormValid.value && !isLoading.value) {
        handlePurchase();
    }
};
</script>
<style scoped lang="scss">
.gift-main-container {
    @apply flex items-start self-stretch flex-col w-full;

    .inputs {
        @apply flex flex-col items-start gap-6 w-full;

        .start-input {
            @apply flex flex-col lg:flex-row items-start gap-6 w-full;

            .name-div {
                @apply flex flex-col items-start gap-1 flex-1 w-full;

                label {
                    @apply text-zinc-700 font-inter text-sm font-medium leading-5;
                }
            }

            .amount-div {
                @apply flex flex-col items-start gap-1 flex-1 w-full;

                label {
                    @apply text-zinc-700 font-inter text-sm font-medium leading-5;
                }

                .amount-container {
                    @apply flex items-start self-stretch rounded-sm border;

                    &.error {
                        @apply border-red-300;

                        &:focus-within {
                            @apply border-red-500;

                            .amount-error {
                                @apply border-r-red-500;
                            }
                        }
                    }

                    .amount-error {
                        @apply flex p-2 px-3 items-center border-r;

                        p {
                            @apply text-gray-600 font-inter text-base font-normal leading-6;
                        }
                    }
                }
            }
        }
    }
}
.name-input {
    @apply flex p-2.5 px-3 items-center w-full gap-2 self-stretch rounded-sm border border-zinc-300 bg-white;
}
.amount-input {
    @apply w-full flex p-2.5 px-3 w-full items-center gap-2 self-stretch rounded-sm border !border-white bg-white;
}
.email-input {
    @apply w-full p-2 px-3 items-center gap-2 rounded-sm bg-white;
}
.amount-warning {
    @apply text-red-500 font-inter text-xs font-normal leading-4 mt-1;
}
.amount-info {
    @apply text-zinc-500 font-inter text-xs font-normal leading-4 mt-1;
}
.email-div {
    @apply flex flex-col items-start gap-1.5 w-full;

    .email-sub-div {
        @apply flex flex-col items-start gap-1 w-full;

        label {
            @apply text-zinc-700 font-inter text-sm font-medium leading-5;
        }
    }
}
.summary-section {
    @apply flex py-6 items-center gap-1 self-stretch;

    .summary-header {
        @apply flex items-center gap-2 flex-1;

        p {
            @apply text-zinc-900 font-inter text-lg font-semibold leading-8;
        }
    }
}
.summary-footer {
    @apply flex justify-between items-center self-stretch py-3 border-b border-t border-dashed border-zinc-300;

    span {
        @apply text-zinc-800 font-inter text-base font-semibold leading-8;
    }
    p {
        @apply text-zinc-950 text-right font-inter text-base font-medium leading-8;
    }
}
.validation-message {
    @apply flex gap-1 mt-1;

    span {
        @apply text-red-600 font-inter text-sm font-normal leading-5;
    }
}
</style>
